(() => {
  const SECTION_SELECTOR = '[data-careers-who-builds-it-section]';
  const SLIDER_SELECTOR = '[data-cwbi-slider]';
  const DEPARTMENT_SELECTOR = '[data-cwbi-department]';
  const COUNTER_SELECTOR = '[data-cwbi-counter]';
  const CURRENT_SELECTOR = '[data-cwbi-counter-current]';
  const TOTAL_SELECTOR = '[data-cwbi-counter-total]';
  const MOBILE_QUERY = '(max-width: 749px)';

  class CareersWhoBuildsIt {
    constructor(section) {
      this.section = section;
      this.mediaQuery = window.matchMedia(MOBILE_QUERY);
      this.swipers = new Map();
      this.retryCount = 0;
      this.retryTimer = null;

      this.handleMediaChange = this.handleMediaChange.bind(this);

      this.init();
      this.bindEvents();
    }

    init() {
      if (this.mediaQuery.matches) {
        this.enableMobileSliders();
      } else {
        this.disableMobileSliders();
      }
    }

    bindEvents() {
      if (this.mediaQuery.addEventListener) {
        this.mediaQuery.addEventListener('change', this.handleMediaChange);
      } else {
        this.mediaQuery.addListener(this.handleMediaChange);
      }
    }

    unbindEvents() {
      if (this.mediaQuery.removeEventListener) {
        this.mediaQuery.removeEventListener('change', this.handleMediaChange);
      } else {
        this.mediaQuery.removeListener(this.handleMediaChange);
      }
    }

    handleMediaChange() {
      this.init();
    }

    enableMobileSliders() {
      const sliders = this.section.querySelectorAll(SLIDER_SELECTOR);

      sliders.forEach((slider) => {
        const department = slider.closest(DEPARTMENT_SELECTOR);
        const counter = department ? department.querySelector(COUNTER_SELECTOR) : null;
        const slides = slider.querySelectorAll('.swiper-slide');
        const totalSlides = slides.length;

        this.prepareCounter(counter, totalSlides);
      });

      if (typeof window.Swiper === 'undefined') {
        this.retrySwiperInit();
        return;
      }

      sliders.forEach((slider) => {
        if (this.swipers.has(slider)) return;

        const department = slider.closest(DEPARTMENT_SELECTOR);
        const counter = department ? department.querySelector(COUNTER_SELECTOR) : null;

        slider.setAttribute('tabindex', '0');

        const swiper = new window.Swiper(slider, {
          slidesPerView: 'auto',
          spaceBetween: 8,
          grabCursor: true,
          watchOverflow: true,
          threshold: 5,
          speed: 450,
          keyboard: {
            enabled: true,
            onlyInViewport: true
          },
          a11y: {
            enabled: true,
            slideRole: 'group',
            slideLabelMessage: '{{index}} / {{slidesLength}}'
          },
          on: {
            init: (instance) => {
              this.updateCounter(instance, counter);
            },
            slideChange: (instance) => {
              this.updateCounter(instance, counter);
            },
            transitionEnd: (instance) => {
              this.updateCounter(instance, counter);
            },
            progress: (instance) => {
              this.updateCounter(instance, counter);
            },
            reachEnd: (instance) => {
              this.updateCounter(instance, counter);
            },
            fromEdge: (instance) => {
              this.updateCounter(instance, counter);
            },
            resize: (instance) => {
              this.updateCounter(instance, counter);
            }
          }
        });

        this.swipers.set(slider, swiper);
      });
    }

    retrySwiperInit() {
      window.clearTimeout(this.retryTimer);

      if (this.retryCount >= 20) return;

      this.retryCount += 1;

      this.retryTimer = window.setTimeout(() => {
        this.enableMobileSliders();
      }, 150);
    }

    disableMobileSliders() {
      this.swipers.forEach((swiper) => {
        if (swiper && typeof swiper.destroy === 'function') {
          swiper.destroy(true, true);
        }
      });

      this.swipers.clear();

      const counters = this.section.querySelectorAll(COUNTER_SELECTOR);

      counters.forEach((counter) => {
        const current = counter.querySelector(CURRENT_SELECTOR);

        if (current) {
          current.textContent = '01';
        }

        counter.style.setProperty('--cwbi-counter-progress', '0%');
      });
    }

    prepareCounter(counter, totalSlides) {
      if (!counter) return;

      const current = counter.querySelector(CURRENT_SELECTOR);
      const total = counter.querySelector(TOTAL_SELECTOR);

      if (current) {
        current.textContent = '01';
      }

      if (total) {
        total.textContent = this.formatNumber(totalSlides);
      }

      if (totalSlides <= 1) {
        counter.classList.add('is-hidden');
        counter.style.setProperty('--cwbi-counter-progress', '100%');
      } else {
        counter.classList.remove('is-hidden');

        const initialProgress = 100 / totalSlides;
        counter.style.setProperty('--cwbi-counter-progress', `${initialProgress}%`);
      }
    }

    updateCounter(swiper, counter) {
      if (!swiper || !counter) return;

      const current = counter.querySelector(CURRENT_SELECTOR);
      const total = counter.querySelector(TOTAL_SELECTOR);
      const totalSlides = swiper.slides ? swiper.slides.length : 0;

      if (!totalSlides) return;

      let currentIndex = swiper.activeIndex + 1;

      if (swiper.isEnd) {
        currentIndex = totalSlides;
      }

      currentIndex = Math.min(currentIndex, totalSlides);

      const progress = swiper.isEnd
        ? 100
        : Math.min((currentIndex / totalSlides) * 100, 100);

      if (current) {
        current.textContent = this.formatNumber(currentIndex);
      }

      if (total) {
        total.textContent = this.formatNumber(totalSlides);
      }

      counter.style.setProperty('--cwbi-counter-progress', `${progress}%`);
    }

    formatNumber(number) {
      return String(number || 0).padStart(2, '0');
    }

    destroy() {
      window.clearTimeout(this.retryTimer);
      this.disableMobileSliders();
      this.unbindEvents();
    }
  }

  const initSection = (section) => {
    if (!section || section.__careersWhoBuildsIt) return;

    section.__careersWhoBuildsIt = new CareersWhoBuildsIt(section);
  };

  const destroySection = (section) => {
    if (!section || !section.__careersWhoBuildsIt) return;

    section.__careersWhoBuildsIt.destroy();
    section.__careersWhoBuildsIt = null;
  };

  const initAllSections = (container = document) => {
    const sections = container.querySelectorAll(SECTION_SELECTOR);
    sections.forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllSections());
  } else {
    initAllSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAllSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector(SECTION_SELECTOR);
    destroySection(section);
  });
})();